import React, { useMemo } from "react";

import { T } from "services/lang/LanguageService";
import {
  ChartDataset,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { AdvancedProps, Datapoint } from "../sparkline/SparkLineTypes";
import "./SparkLineChart.scss";
import "chartjs-adapter-date-fns";

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Filler);

type SparkLineChartProps = {
  dataset: string;
  advancedProps: AdvancedProps;
  lineColor?: string;
  lineWidth?: number;
  chartWidth?: number;
  chartHeight?: number;
  minYAxis?: number;
  maxYAxis?: number;
  isInlineMultipleCharts?: boolean;
  minValues: Datapoint[];
  maxValues: Datapoint[];
  baselineValues: Datapoint[];
};

const options = (showYAxis = false, minYAxis?: number, maxYAxis?: number): ChartOptions<"line"> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 0,
  },
  plugins: {
    tooltip: {
      enabled: false,
    },
    legend: {
      display: false,
    },
  },
  interaction: {
    intersect: false,
  },
  scales: {
    x: {
      display: false,
      type: "time",
    },
    y: {
      display: showYAxis && "auto",
      min: minYAxis,
      max: maxYAxis,
    },
  },
});

const SparkLineChart: React.FC<SparkLineChartProps> = ({
  dataset,
  advancedProps,
  lineColor,
  lineWidth,
  chartWidth,
  chartHeight,
  minYAxis,
  maxYAxis,
  isInlineMultipleCharts,
  minValues,
  maxValues,
  baselineValues,
}) => {
  const hasDuplicatedDatasets = useMemo(() => {
    const datasetIds = [
      dataset,
      advancedProps.baseline?.dataset,
      advancedProps.minLine?.dataset,
      advancedProps.maxLine?.dataset,
    ].filter(Boolean);

    const uniqueSet = new Set(datasetIds);
    return uniqueSet.size !== datasetIds.length;
  }, [advancedProps]);

  const baselineDataChart: ChartDataset<"line", Datapoint[]> = {
    data: baselineValues,
    borderColor: advancedProps.baseline?.color,
    borderWidth: advancedProps.baseline?.width,
    pointStyle: false,
  };
  const minValueLine: ChartDataset<"line", Datapoint[]> = {
    data: minValues,
    borderColor: advancedProps.minLine?.color,
    borderWidth: 1,
    borderDash: [2, 5],
    pointStyle: false,
  };
  const maxValueLine: ChartDataset<"line", Datapoint[]> = {
    data: maxValues,
    borderColor: advancedProps.maxLine?.color,
    borderWidth: 1,
    borderDash: [3, 5],
    pointStyle: false,
  };

  const lineOptions = options(advancedProps.showYAxis, minYAxis, maxYAxis);

  const currentChartWidth = chartWidth ? `${chartWidth}px` : "100%";

  const getChartHeight = () => {
    if (isInlineMultipleCharts) {
      return "100%";
    }
    if (advancedProps.isMultipleCharts) {
      return "145px";
    }
    return chartHeight ? `${chartHeight}px` : "100%";
  };

  const style = {
    width: currentChartWidth,
    height: getChartHeight(),
    backgroundColor: advancedProps.isMultipleCharts ? "white" : undefined,
    borderRadius: advancedProps.isMultipleCharts ? "5px" : undefined,
    padding: advancedProps.isMultipleCharts ? "10px 5px" : undefined,
  };

  return (
    <div style={style} className="relative spark-line-chart">
      <Line
        data={{
          datasets: [
            {
              data: advancedProps.data,
              borderColor: "transparent",
              borderWidth: 0,
              pointStyle: false,
              fill: {
                target: 4,
                below: advancedProps.minLine?.color,
                above: "transparent",
              },
            },
            {
              data: advancedProps.data,
              borderColor: "transparent",
              borderWidth: 0,
              pointStyle: false,
              fill: {
                target: 5,
                below: "transparent",
                above: advancedProps.maxLine?.color,
              },
            },
            {
              data: advancedProps.data,
              borderColor: lineColor,
              borderWidth: lineWidth,
              pointStyle: false,
              fill: {
                target: 3,
                above: advancedProps.baseline?.fillAboveColor,
                below: advancedProps.baseline?.fillBelowColor,
              },
            },
            baselineDataChart,
            minValueLine,
            maxValueLine,
          ],
        }}
        options={lineOptions}
      />
      {hasDuplicatedDatasets && (
        <div className="error-message">{T("error.message.there-are-duplicated-datasets-in-the-config")}</div>
      )}
    </div>
  );
};

export default SparkLineChart;
